import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatHash,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

const applicationGlobals = new Set(['rawData']);

export function browserGlobalNames(globalObject: object): string[] {
  return Object.getOwnPropertyNames(globalObject)
    .filter((name) => !applicationGlobals.has(name))
    .sort();
}

function normalizeError(error: unknown): RawData {
  const detail = domError(error);
  return { name: detail.name, message: detail.message };
}

function descriptorShape(object: object, key: string): RawData | null {
  const descriptor = Object.getOwnPropertyDescriptor(object, key);
  if (!descriptor) {
    return null;
  }
  return {
    configurable: descriptor.configurable,
    enumerable: descriptor.enumerable,
    writable: 'writable' in descriptor ? descriptor.writable : null,
    getterText: descriptor.get
      ? Function.prototype.toString.call(descriptor.get)
      : null,
    setterText: descriptor.set
      ? Function.prototype.toString.call(descriptor.set)
      : null,
  };
}

async function collect(): Promise<RawData> {
  const globals = browserGlobalNames(window);
  const navigatorKeys = Object.getOwnPropertyNames(Navigator.prototype).sort();
  const htmlElementKeys = Object.getOwnPropertyNames(
    HTMLElement.prototype,
  ).sort();
  const descriptors = {
    webdriver: descriptorShape(Navigator.prototype, 'webdriver'),
    userAgent: descriptorShape(Navigator.prototype, 'userAgent'),
    isTrusted: descriptorShape(Event.prototype, 'isTrusted'),
    value: descriptorShape(HTMLInputElement.prototype, 'value'),
  };
  const nativeFunctionSamples = {
    querySelector: Function.prototype.toString.call(
      Document.prototype.querySelector,
    ),
    getContext: Function.prototype.toString.call(
      HTMLCanvasElement.prototype.getContext,
    ),
    createOffer: Function.prototype.toString.call(
      RTCPeerConnection.prototype.createOffer,
    ),
  };
  let illegalInvocation: RawData | null = null;
  try {
    const getter = Object.getOwnPropertyDescriptor(
      Navigator.prototype,
      'userAgent',
    )?.get;
    getter?.call({});
  } catch (error) {
    illegalInvocation = normalizeError(error);
  }
  return {
    globals,
    navigatorKeys,
    htmlElementKeys,
    descriptors,
    nativeFunctionSamples,
    illegalInvocationErrors: { navigatorUserAgent: illegalInvocation },
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validation([]);
}

export const webIdlSurfaceTest: TestDefinition = {
  id: 'webApiSurface',
  title: 'Web API surface',
  category: 'shared',
  format,
  run,
  validate,
};
